
import { supabase } from '../client';
import { Form, FormField } from '@/components/dashboard/shops/types';
import { v4 as uuidv4 } from 'uuid';

export async function getFormsByProductId(productId: string): Promise<Form[]> {
  try {
    if (!productId) {
      console.error('[getFormsByProductId] No product ID provided');
      return [];
    }
    
    console.log(`[getFormsByProductId] Fetching products with ID: ${productId}`);
    
    // D'abord on récupère le form_id du produit
    const { data: product, error: productError } = await supabase
      .from('products')
      .select('form_id')
      .eq('id', productId)
      .single();
    
    if (productError || !product || !product.form_id) {
      console.log('[getFormsByProductId] Produit sans form_id ou erreur:', productError);
      return [];
    }
    
    // Ensuite on récupère le formulaire
    const { data, error } = await supabase
      .from('forms')
      .select('*')
      .eq('id', product.form_id);
    
    if (error) {
      console.error('[getFormsByProductId] Error fetching forms:', error);
      throw error;
    }
    
    console.log(`[getFormsByProductId] Retrieved ${data?.length || 0} forms for product ${productId}`);
    return data || [];
  } catch (error) {
    console.error('[getFormsByProductId] Exception:', error);
    return [];
  }
}

export function createDefaultFormFields(): FormField[] {
  return [
    {
      id: uuidv4(),
      name: 'Nom',
      label: 'Nom Complet',
      type: 'text',
      enabled: true,
      required: true
    },
    {
      id: uuidv4(),
      name: 'Téléphone',
      label: 'Numéro De Téléphone',
      type: 'text',
      enabled: true,
      required: true
    },
    {
      id: uuidv4(),
      name: 'Email',
      label: 'Email',
      type: 'text',
      enabled: true,
      required: false
    },
    {
      id: uuidv4(),
      name: 'Wilaya',
      label: 'Wilaya',
      type: 'text',
      enabled: true,
      required: true
    },
    {
      id: uuidv4(),
      name: 'Commune',
      label: 'Commune',
      type: 'text',
      enabled: true,
      required: true
    },
    {
      id: uuidv4(),
      name: 'Adresse',
      label: 'Adresse',
      type: 'textarea',
      enabled: true,
      required: false
    },
    {
      id: uuidv4(),
      name: 'Note',
      label: 'Note',
      type: 'textarea',
      enabled: true,
      required: false
    }
  ];
}

export async function migrateShopFormsToFormTable(shopId: string, organizationId: string): Promise<void> {
  // Cette fonction est utilisée pour migrer d'anciens formulaires vers la nouvelle structure
  console.log(`[migrateShopFormsToFormTable] Starting migration for shop ${shopId}`);
  
  try {
    // Vérifier si des formulaires existent déjà pour cette boutique
    const { data: existingForms, error: checkError } = await supabase
      .from('forms')
      .select('id')
      .eq('shop_id', shopId);
    
    if (checkError) {
      console.error('[migrateShopFormsToFormTable] Error checking existing forms:', checkError);
      return;
    }
    
    // Si des formulaires existent déjà, ne pas migrer
    if (existingForms && existingForms.length > 0) {
      console.log(`[migrateShopFormsToFormTable] Forms already exist for shop ${shopId}, skipping migration`);
      return;
    }
    
    // Créer un formulaire par défaut si nécessaire
    await createDefaultShopForm(shopId, organizationId);
    
    console.log(`[migrateShopFormsToFormTable] Migration completed for shop ${shopId}`);
  } catch (error) {
    console.error('[migrateShopFormsToFormTable] Error during migration:', error);
  }
}

export async function createDefaultShopForm(shopId: string, organizationId: string): Promise<Form | null> {
  try {
    console.log(`[createDefaultShopForm] Creating default form for shop: ${shopId}`);
    
    const defaultForm = {
      name: 'Formulaire de commande standard',
      shop_id: shopId,
      organization_id: organizationId,
      fields: createDefaultFormFields(),
      cta_button_text: 'Commander maintenant',
      product_ids: []
    };
    
    const { data, error } = await supabase
      .from('forms')
      .insert([defaultForm])
      .select()
      .single();
    
    if (error) {
      console.error('[createDefaultShopForm] Error creating default form:', error);
      return null;
    }
    
    console.log('[createDefaultShopForm] Default form created:', data);
    return data;
  } catch (error) {
    console.error('[createDefaultShopForm] Exception creating default form:', error);
    return null;
  }
}
